import { Avatar, Box, Button, Chip, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FC, useEffect, useState } from "react";
import { useStyles } from "./CompanyHeader.styles";
import { useStore } from "src/Shared/Hooks";
import { STATUS_STYLES } from "./CompanyHeader.config";
import { Link, useLocation } from "react-router-dom";
import { observer } from "mobx-react";
import { isProspectStage } from "../../../Enums/ClientStatus.enum";
import { employeeCountText } from "../../../Tools/DisplayValue";
import { isClientArchived } from "../../../Tools/ArchivedClient";

const CompanyHeader: FC = () => {
  const location = useLocation();
  const isArchivedMode = location.pathname.includes("/archived");

  const {
    rootStore: {
      companyStore: { companyOverview, archivedCompany },
    },
  } = useStore();
  const classes = useStyles();
  const getInitials = (name?: string) => {
    if (!name) return "NC";
    return name
      .split(" ")
      .map((x) => x[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  const [selectedClientStatus, setSelectedClientStatus] = useState<number | "">(
    "",
  );
  useEffect(() => {
    if (isArchivedMode) {
      setSelectedClientStatus("");
    } else if (
      companyOverview?.clientStatusID !== undefined &&
      companyOverview?.clientStatusID !== null
    ) {
      setSelectedClientStatus(companyOverview?.clientStatusID);
    }
  }, [companyOverview?.clientStatusID, isArchivedMode]);

  /**
   * An archived client opened through its own URL reads as archived, like the legacy archive route.
   *
   * The chip used to show the client status it held when it was deleted — Onboarding, say — so an
   * archived record was indistinguishable from a live one and nothing on screen explained why its
   * sections were locked (CR.11).
   */
  const isArchivedClient = isClientArchived(companyOverview);
  const showsAsArchived = isArchivedMode || isArchivedClient;

  const statusStyles = showsAsArchived
    ? STATUS_STYLES["archived"]
    : STATUS_STYLES[companyOverview?.status?.toLowerCase() || "default"] ||
      STATUS_STYLES["default"];

  const name = isArchivedMode
    ? archivedCompany?.companyName
    : companyOverview?.companyName;

  const totalStaff = isArchivedMode
    ? archivedCompany?.totalStaffNumber
    : companyOverview?.totalStaffNumber;

  const status = showsAsArchived ? "Archived" : companyOverview?.status;

  // Back always leaves the record to the grid the client belongs to (CR.10). An archived client is
  // excluded from every grid regardless of the status it held when archived, so "Back to Prospects"
  // — read from that now-stale status — pointed at a grid that would never contain it (task 5844).
  const backToProspects =
    !showsAsArchived && isProspectStage(companyOverview?.clientStatusID);

  return (
    <Box className={classes.root}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          component={Link}
          to={backToProspects ? "/company/prospects" : "/company"}
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {backToProspects ? "Back to Prospects" : "Back to Home"}
        </Button>
        <Avatar className={classes.avtar}>
          {getInitials(name)}
        </Avatar>

        <Box>
          <Typography
            sx={{ fontSize: 20, fontWeight: 700, color: "#1f2d3d" }}
            component={companyOverview?.companyRowID ? Link : "span"}
            to={
              companyOverview?.companyRowID
                ? `/company/${companyOverview.companyRowID}/overview`
                : undefined
            }
            style={{ textDecoration: "none", color: "#1f2d3d" }}
          >
            {name || (isArchivedMode ? "Loading Archived..." : "New Client")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 14, color: "#6f7782" }}>
              {companyOverview?.franchiseName || "No region"}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#6f7782" }}>·</Typography>
            <Typography sx={{ fontSize: 14, color: "#6f7782" }}>
              {companyOverview?.primaryCompanyRelationshipManager || "Unassigned"}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#6f7782" }}>·</Typography>
            <Typography sx={{ fontSize: 14, color: "#6f7782" }}>
              {employeeCountText(totalStaff)}
            </Typography>

            <Chip
              label={status || "TBD"}
              size="small"
              sx={{
                ml: 1,
                backgroundColor: statusStyles.chipBg,
                color: statusStyles.chipColor,
                fontWeight: 600,
                height: 24,
                "& .MuiChip-label": {
                  px: 1.2,
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default observer(CompanyHeader);
